import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { MapPin, Wind, Droplets } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COORDS_KEY = 'bb_last_coords';

/**
 * Weather widget — uses browser Geolocation (one-tap permission) and
 * Open-Meteo on the backend. Caches the last known coords in localStorage
 * to avoid re-prompting on every dashboard mount.
 */
export function WeatherWidget() {
    const { language } = useLanguage();
    const [weather, setWeather] = useState(null);
    const [permissionPrompt, setPermissionPrompt] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetchWeather = async (lat, lon) => {
            try {
                const res = await axios.get(
                    `${API}/weather/current?lat=${lat}&lon=${lon}&language=${language}`);
                if (cancelled) return;
                if (res.data && !res.data.error) setWeather(res.data);
            } catch (_e) { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        };

        const cached = localStorage.getItem(COORDS_KEY);
        if (cached) {
            try {
                const { lat, lon, t } = JSON.parse(cached);
                // Refresh cached coords once per hour at most
                if (Date.now() - t < 60 * 60 * 1000) {
                    fetchWeather(lat, lon);
                    return undefined;
                }
            } catch (_e) { /* ignore */ }
        }

        if (!navigator.geolocation) {
            // No setLoading here — loading stays true so widget renders null.
            // Same UX as failed weather fetch.
            return undefined;
        }
        // Ask once — non-blocking, fails silently if denied.
        setPermissionPrompt(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setPermissionPrompt(false);
                const lat = pos.coords.latitude;
                const lon = pos.coords.longitude;
                localStorage.setItem(COORDS_KEY, JSON.stringify({ lat, lon, t: Date.now() }));
                fetchWeather(lat, lon);
            },
            () => { setPermissionPrompt(false); setLoading(false); },
            { timeout: 8000, maximumAge: 60 * 60 * 1000 }
        );
        return () => { cancelled = true; };
    }, [language]);

    if (loading || permissionPrompt) return null;
    if (!weather) return null;

    return (
        <Card className="overflow-hidden border bg-gradient-to-br from-sky-500/5 via-card to-card" data-testid="weather-widget">
            <CardContent className="p-3">
                <div className="flex items-center gap-3">
                    <div className="text-4xl shrink-0 leading-none" aria-hidden="true">
                        {weather.emoji}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold leading-none" data-testid="weather-temp">
                                {weather.temperature}°
                            </span>
                            <span className="text-xs text-muted-foreground truncate">
                                {weather.label}
                            </span>
                        </div>
                        <div className="flex items-center gap-2.5 mt-1 text-[10px] text-muted-foreground">
                            {weather.day_min != null && (
                                <span>
                                    {weather.day_min}° – {weather.day_max}°
                                </span>
                            )}
                            {weather.humidity != null && (
                                <span className="inline-flex items-center gap-0.5">
                                    <Droplets className="w-2.5 h-2.5" /> {weather.humidity}%
                                </span>
                            )}
                            {weather.wind_kmh != null && (
                                <span className="inline-flex items-center gap-0.5">
                                    <Wind className="w-2.5 h-2.5" /> {weather.wind_kmh} km/h
                                </span>
                            )}
                            <span className="inline-flex items-center gap-0.5 ml-auto opacity-60">
                                <MapPin className="w-2.5 h-2.5" />
                            </span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
